import type { CSSProperties } from 'react';

/**
 * DurationTimeline — TCP 콘솔 타임라인 (connect/write/read/total ms 수평 바) (§Δ.3)
 * segments 는 connect/write/read 순 — totalMs 대비 비율로 폭을 계산.
 */
export function DurationTimeline({
  segments,
  totalMs,
  totalLabel,
}: {
  segments: { label: string; ms: number; color: string }[];
  totalMs: number;
  totalLabel: string;
}) {
  const safeTotal = totalMs > 0 ? totalMs : 1;
  return (
    <div>
      <div style={barTrackStyle} role="img" aria-label={`${totalLabel}: ${totalMs}ms`}>
        {segments.map((seg) => (
          <div
            key={seg.label}
            title={`${seg.label}: ${seg.ms}ms`}
            style={{
              width: `${Math.max((seg.ms / safeTotal) * 100, seg.ms > 0 ? 2 : 0)}%`,
              background: seg.color,
              height: '100%',
            }}
          />
        ))}
      </div>
      <div style={legendRowStyle}>
        {segments.map((seg) => (
          <span key={seg.label} style={legendItemStyle}>
            <span style={{ ...legendDotStyle, background: seg.color }} />
            {seg.label}: {seg.ms}ms
          </span>
        ))}
        <span style={{ ...legendItemStyle, fontWeight: 600 }}>
          {totalLabel}: {totalMs}ms
        </span>
      </div>
    </div>
  );
}

/**
 * OffsetTimeline — gRPC streaming 응답 프레임 순차 타임라인 (각 receivedOffsetMs) (§Δ.3)
 */
export function OffsetTimeline({
  frames,
  totalMs,
}: {
  frames: { message: string; receivedOffsetMs: number }[];
  totalMs: number;
}) {
  const safeTotal = totalMs > 0 ? totalMs : 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
      <div style={barTrackStyle}>
        {frames.map((f, i) => (
          <span
            key={i}
            title={`#${i + 1} +${f.receivedOffsetMs}ms`}
            style={{
              position: 'absolute',
              left: `${Math.min((f.receivedOffsetMs / safeTotal) * 100, 100)}%`,
              width: 3,
              height: '100%',
              background: 'var(--color-brand-primary)',
            }}
          />
        ))}
      </div>
      <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
        {frames.map((f, i) => (
          <li key={i} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            <code style={{ fontFamily: 'var(--font-family-mono)' }}>
              #{i + 1} +{f.receivedOffsetMs}ms
            </code>{' '}
            — {f.message}
          </li>
        ))}
      </ol>
    </div>
  );
}

const barTrackStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  width: '100%',
  height: 12,
  borderRadius: 'var(--radius-sm)',
  overflow: 'hidden',
  background: 'var(--color-surface-muted)',
  border: '1px solid var(--color-surface-border)',
};

const legendRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--spacing-3)',
  marginTop: 'var(--spacing-2)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

const legendItemStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-1)',
};

const legendDotStyle: CSSProperties = {
  display: 'inline-block',
  width: 8,
  height: 8,
  borderRadius: '50%',
};
